import { useQuery, type QueryClient } from '@tanstack/react-query'
import { InterventionFullResponse } from '@depanneo/types'
import { getInterventionFull } from '@/api/interventions-api'
import { interventionKeys } from '../query-keys'
import { QUERY_TIMING } from '../constants'
import { queryClient } from '@/main'
import { retryUnlessRefused } from '@/shared/utils/network-error-utils'

/**
 * `retry` for every `full(id)` fetch (#2192). A 404/403 is the API refusing this
 * id for this user (another org's, deleted, hand-edited) and will answer the same
 * on every attempt, so it settles at once instead of holding the skeleton through
 * the default three backoff retries — each one another heavy `/full` call. Set on
 * the loader's prefetch too: whichever call starts the fetch owns its retryer.
 */
export const retryInterventionFull = retryUnlessRefused

/**
 * Fetches the aggregate endpoint and seeds detail/counts caches.
 * Shared between the route loader (prefetch) and the hook (refetch).
 */
export const fetchInterventionFull = async (
  interventionId: string
): Promise<InterventionFullResponse> => {
  const data = await getInterventionFull(interventionId)

  queryClient.setQueryData(
    interventionKeys.detail(interventionId),
    data.intervention
  )
  queryClient.setQueryData(interventionKeys.counts(interventionId), data.counts)

  return data
}

/**
 * Prefetches the full intervention aggregate data into the query cache.
 * Use in loaders and hover handlers to warm the cache before navigation.
 */
export const prefetchInterventionFull = (
  qc: QueryClient,
  interventionId: string
) => {
  qc.prefetchQuery({
    queryKey: interventionKeys.full(interventionId),
    queryFn: () => fetchInterventionFull(interventionId),
    staleTime: QUERY_TIMING.STALE_TIME.FULL,
    retry: retryInterventionFull,
  })
}

export const useGetInterventionFull = (interventionId?: string) => {
  return useQuery({
    queryKey: interventionKeys.full(interventionId!),
    queryFn: () => fetchInterventionFull(interventionId!),
    staleTime: QUERY_TIMING.STALE_TIME.FULL,
    gcTime: QUERY_TIMING.GC_TIME,
    retry: retryInterventionFull,
    enabled: !!interventionId,
  })
}
